import { generateId } from '@freelancer/datastore/testing';
import { flatten, isDefined } from '@freelancer/utils';
import { ResourceTypeApi } from 'api-typings/resources/metadata';
import { Enterprise } from '../enterprise';
import {
  CustomFieldInfo,
  CustomFieldInfoConfiguration,
  CustomFieldValue,
  FieldType,
  FieldValue,
} from './custom-field-info-configurations.model';

// Generate Options

interface GenerateCustomFieldInfoOptions {
  readonly name: string;
  readonly fieldType: FieldType;
  readonly customFieldInfoId?: number;
  readonly isArray?: boolean;
  readonly description?: string;
  readonly resourceType?: ResourceTypeApi;
  readonly timeCreated?: number;
}

export type GenerateCustomFieldInfoConfigurationOptions = {
  readonly configurationId?: number;
  readonly enterpriseId?: number;
  readonly defaultValue?: FieldValue;
  readonly searchable?: boolean;
  readonly timeEnabled?: number;
} & GenerateCustomFieldInfoOptions;

export type GenerateCustomFieldValueOptions = {
  readonly resourceId: number;
  readonly customFieldInfoConfigurationId?: number;
  readonly isDefaultValue?: boolean;
} & FieldValue;

/**
 * Provides options to seed customFieldValue and customFieldInfoConfigurations
 * The custom field is a field and value by nature thus this model. It serves to encapsulate
 * the field's options and value's options of the custom field.
 */
export type CustomFieldValueAndConfigurationOptions = {
  /**
   * Used to seed the customFieldInfoConfigurations
   */
  readonly fieldOptions: GenerateCustomFieldInfoConfigurationOptions;
} & (
  | {
      /**
       * Used to seed the CustomFieldValue of a resource
       */
      readonly valueOptions?: GenerateCustomFieldValueOptions;
      readonly isArray: false;
    }
  | {
      /**
       * Used to seed the CustomFieldValue with array value of a resource
       */
      readonly valueOptions?: ReadonlyArray<GenerateCustomFieldValueOptions>;
      readonly isArray: true;
    }
);

function generateCustomFieldInfo({
  name,
  fieldType,
  customFieldInfoId,
  isArray = false,
  description = 'test field description',
  resourceType = ResourceTypeApi.USER,
  timeCreated = Date.now(),
}: GenerateCustomFieldInfoOptions): CustomFieldInfo {
  const id = customFieldInfoId !== undefined ? customFieldInfoId : generateId();

  return {
    id,
    name,
    isArray,
    description,
    resourceType,
    fieldType,
    timeCreated,
  };
}

export function generateCustomFieldInfoConfiguration({
  name,
  description,
  fieldType,
  defaultValue,
  configurationId,
  isArray = false,
  enterpriseId = Enterprise.DELOITTE_DC,
  resourceType = ResourceTypeApi.USER,
  searchable = false,
  timeCreated = Date.now(),
  timeEnabled = Date.now(),
}: GenerateCustomFieldInfoConfigurationOptions): CustomFieldInfoConfiguration {
  const id = configurationId !== undefined ? configurationId : generateId();

  return {
    id,
    enterpriseId,
    customFieldInfo: generateCustomFieldInfo({
      name,
      description,
      isArray,
      resourceType,
      fieldType,
    }),
    defaultValue,
    searchable,
    resourceType,
    timeCreated,
    timeEnabled,
  };
}

export function generateCustomFieldValue({
  type,
  value,
  customFieldInfoConfigurationId = generateId(),
  resourceId = generateId(),
  isDefaultValue = false,
}: GenerateCustomFieldValueOptions): CustomFieldValue {
  return {
    customFieldInfoConfigurationId,
    resourceId,
    isDefaultValue,
    type,
    value,
  } as CustomFieldValue;
}

export function generateCustomFieldValues(
  options: ReadonlyArray<GenerateCustomFieldValueOptions>,
): ReadonlyArray<CustomFieldValue> {
  return options.map(option => generateCustomFieldValue(option));
}

export function generateCustomFieldInfoConfigurationObjects(
  customFieldInfoConfigurationArray: ReadonlyArray<
    GenerateCustomFieldInfoConfigurationOptions
  >,
): ReadonlyArray<CustomFieldInfoConfiguration> {
  return customFieldInfoConfigurationArray.map(customFieldInfoConfiguration =>
    generateCustomFieldInfoConfiguration(customFieldInfoConfiguration),
  );
}
/**
 * Used to generate a real model CustomFieldValues from field and value pair.
 * It will then be used by seed to intialize a resource such as user.
 * CustomFieldsValue is the value of the CustomFieldInfoConfiguration.
 */
export function getCustomFieldValues(
  fieldAndValueOptions: ReadonlyArray<CustomFieldValueAndConfigurationOptions>,
): ReadonlyArray<CustomFieldValue> {
  return flatten(
    fieldAndValueOptions
      .map(options => {
        if (!options.valueOptions) {
          return undefined;
        }
        return options.isArray
          ? generateCustomFieldValues(options.valueOptions)
          : [generateCustomFieldValue(options.valueOptions)];
      })
      .filter(isDefined),
  );
}

/**
 * Grabs the CustomFieldInfoConfigurationOption from
 * the combined field and value option or CustomFieldValueAndConfigurationOptions.
 * Will be used to seed the CustomFieldInfoConfiguration.
 */
export function getCustomFieldInfoConfigurationOptions(
  fieldAndValueOptions: ReadonlyArray<CustomFieldValueAndConfigurationOptions>,
): ReadonlyArray<GenerateCustomFieldInfoConfigurationOptions> {
  return fieldAndValueOptions.map(options => options.fieldOptions);
}

// Mixins for CustomFieldValueAndConfigurationOptions

export function firstName(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 1,
      name: 'first_name',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 1,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function lastName(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 2,
      name: 'last_name',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 2,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function level(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 3,
      name: 'level',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 3,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function legalEntityAlignment(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 4,
      name: 'legal_entity_alignment',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 4,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function practice(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 5,
      name: 'practice',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 5,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function offeringPortfolio(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 6,
      name: 'offering_portfolio',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 6,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function independenceRuleset(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 7,
      name: 'independence_ruleset',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 7,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function phoneNumberWorkCell(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 8,
      name: 'phone_number_work_cell',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 8,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function phoneNumberSkype(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 9,
      name: 'phone_number_skype',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 9,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function memberFirmAlignment(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 10,
      name: 'member_firm_alignment',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 10,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function countryAlignment(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 11,
      name: 'country_alignment',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 11,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function emailAddress(
  resourceId: number,
  value?: string,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: false,
    fieldOptions: {
      configurationId: 12,
      name: 'email_address',
      fieldType,
    },
    valueOptions: value
      ? {
          customFieldInfoConfigurationId: 12,
          resourceId,
          value,
          type: fieldType,
        }
      : undefined,
  };
}

export function certifications(
  resourceId: number,
  values?: ReadonlyArray<string>,
): CustomFieldValueAndConfigurationOptions {
  const fieldType = FieldType.STRING;
  return {
    isArray: true,
    fieldOptions: {
      configurationId: 13,
      isArray: true,
      name: 'certifications',
      fieldType,
    },
    valueOptions: values
      ? values.map(value => ({
          customFieldInfoConfigurationId: 13,
          resourceId,
          value,
          type: fieldType,
        }))
      : undefined,
  };
}
